export interface InspectionFilterSettings {
  start: string;
  end: string;
  min: string;
  max: string;
}

export interface InspectionsSettings {
  conductedFromDate: number;
  conductedToDate: number;
  inspectionDateMax: number;
  inspectionDateMin: number;
}

interface SentrySettings {
  enable: boolean;
  enableBrowserTracingIntegration: boolean;
  enableReplayIntegration: boolean;
}
interface LoginSettings {
  clientId: string;
  url: string;
  issuer: string;
}
interface QcInspectionSettings {
  autoCloseInspection: boolean;
}
interface DefectSettings {
  isDefectImageRequired: boolean;
}

interface SizeCheckConfig {
  passes: number;
  decimalPlaces: number;
  passesTitles: string[];
}

export interface FactoryVerificationSettings {
  enable?: boolean;
  'factory-visit-session-reuse-days'?: number;
  'distance-threshold-meters'?: number;
  'min-gps-accuracy-meters'?: number;
}

/** Stable key names for optional config – use these instead of string literals. */
export const APP_CONFIG_KEY = {
  apiUploadLocations: 'api-upload-locations',
  useText: 'use-text',
  defaultDivision: 'default-division',
  showAssignedLocationInspections: 'show-assigned-location-inspections',
  startInspectionCheck: 'start-inspection-check',
  showInternalOrderRef: 'show-internal-order-ref',
  recordDefect: 'record-defect',
  inspectorCommentLength: 'inspector-comment-length',
  sizeCheck: 'size-check',
  showFactory: 'show-factory',
  mandatoryModuleCheck: 'mandatory-module-check',
  displayLocales: 'display-locales',
  enableDisplayLanguages: 'enable-display-languages',
} as const;

/** Optional app config flags and lists (kebab-case keys from API). */
interface AppConfigKeys {
  [APP_CONFIG_KEY.apiUploadLocations]?: string[];
  [APP_CONFIG_KEY.useText]?: string;
  [APP_CONFIG_KEY.defaultDivision]?: string;
  [APP_CONFIG_KEY.showAssignedLocationInspections]?: boolean;
  [APP_CONFIG_KEY.startInspectionCheck]?: boolean;
  [APP_CONFIG_KEY.showInternalOrderRef]?: boolean;
  [APP_CONFIG_KEY.showFactory]?: boolean;
  [APP_CONFIG_KEY.mandatoryModuleCheck]?: boolean;
  /** When true, show the language selector and allow non-English catalogues. Default: English only. */
  [APP_CONFIG_KEY.enableDisplayLanguages]?: boolean;
  /** Locale ids offered when `enable-display-languages` is true (e.g. `en`, `tr`, `bn`). */
  [APP_CONFIG_KEY.displayLocales]?: string[];
}

export type AppConfigKey = keyof typeof APP_CONFIG_KEY;

export interface AppConfig extends AppConfigKeys {
  sk: string;
  sentry?: SentrySettings;
  inspections?: InspectionsSettings;
  login: LoginSettings;
  qcInspection: QcInspectionSettings;
  'factory-verification'?: FactoryVerificationSettings;
  [APP_CONFIG_KEY.recordDefect]: DefectSettings;
  [APP_CONFIG_KEY.inspectorCommentLength]: number;
  [APP_CONFIG_KEY.sizeCheck]: SizeCheckConfig;
  filters?: string[];
}
